<script lang="ts" setup>
    import {
        ArcElement,
        BarElement,
        CategoryScale,
        Chart as ChartJS,
        Legend,
        LinearScale,
        Tooltip,
        type ChartData,
        type ChartOptions,
        type Plugin
    } from 'chart.js';
    import { Bar, Doughnut } from 'vue-chartjs';
    import type {
        AnalyticsAnswer,
        AnalyticsQuestion,
        AnalyticsResponse,
        AnalyticsSection,
        NormalizedAnswer,
        NormalizedQuestion
    } from '~/types/questionProSurvey';

    const props = defineProps<{
        blok: SurveyResultOverviewStoryblok;
    }>();

    ChartJS.register(ArcElement, BarElement, CategoryScale, LinearScale, Tooltip, Legend);

    const chartContainerRefs = ref<Record<number, HTMLElement | null>>({});
    const runtimeConfig = useRuntimeConfig();
    const headers = useRequestHeaders(['cookie']);

    const currentPage = ref(1);
    const questionsPerPage = 1;

    const colors = [
        'rgb(183, 15, 128)',
        'rgb(243, 147, 36)',
        'rgb(2, 127, 149)',
        'rgb(129, 205, 217)',
        'rgb(235, 149, 149)',
        'rgb(165, 237, 208)',
        'rgb(202, 176, 216)',
        'rgb(255, 214, 126)',
        'rgb(96, 163, 188)',
        'rgb(218, 112, 164)',
        'rgb(139, 198, 154)',
        'rgb(246, 178, 107)',
        'rgb(154, 139, 203)',
        'rgb(120, 196, 176)',
        'rgb(229, 190, 116)'
    ];

    const questionProImageBaseUrl = computed(() => {
        const env = runtimeConfig.public.questionPro.env || 'eu';

        return `https://${env}.questionpro.com`;
    });

    const parseQuestionProTitle = (value = ''): Pick<NormalizedAnswer, 'title' | 'image'> => {
        if (!import.meta.client) {
            return {
                title: value.replaceAll('&nbsp;', ' ').replaceAll('\u00a0', ' ').trim(),
                image: null
            };
        }

        const template = document.createElement('template');
        template.innerHTML = value;

        const rawSrc = template.content.querySelector('img')?.getAttribute('src') ?? null;
        const title = (template.content.textContent ?? '').replaceAll('\u00a0', ' ').trim();

        if (!rawSrc) {
            return {
                title,
                image: null
            };
        }

        if (rawSrc.startsWith('http://') || rawSrc.startsWith('https://')) {
            return {
                title,
                image: rawSrc
            };
        }

        if (rawSrc.startsWith('//')) {
            return {
                title,
                image: `https:${rawSrc}`
            };
        }

        if (rawSrc.startsWith('/')) {
            return {
                title,
                image: `${questionProImageBaseUrl.value}${rawSrc}`
            };
        }

        return {
            title,
            image: `${questionProImageBaseUrl.value}/${rawSrc}`
        };
    };

    const parseQuestionProText = (value = '') => parseQuestionProTitle(value).title;

    const normalizeAnswer = (answer: AnalyticsAnswer): NormalizedAnswer => {
        const parsedTitle = parseQuestionProTitle(answer.title ?? '');

        return {
            title: parsedTitle.title,
            image: parsedTitle.image,
            count: Number(answer.count ?? 0),
            percentage: Number(answer.percentage ?? 0)
        };
    };

    const normalizeQuestion = (section: AnalyticsSection, question: AnalyticsQuestion): NormalizedQuestion | null => {
        const id = Number(question.id ?? question.questionID);

        if (!Number.isFinite(id)) return null;

        const code = typeof question.code === 'string' ? question.code.trim() || null : null;

        const answers = (question.answers ?? []).map(normalizeAnswer);

        if (!answers.length) return null;

        const total = answers.reduce((sum, answer) => sum + answer.count, 0);
        const topAnswer = [...answers].sort((a, b) => b.count - a.count)[0]?.title ?? '';

        return {
            id,
            code,
            title: parseQuestionProText(section.title ?? question.title ?? question.text ?? question.code ?? ''),
            answers,
            total,
            topAnswer
        };
    };

    const surveyId = computed(() => props.blok.surveyID);

    /**
     * Editors enter QuestionPro's question *code* (e.g. "Q1"), which is what
     * they can actually see in QuestionPro's UI - not the internal numeric
     * question id from the analytics response. See MP-1667.
     */
    const selectedQuestionCode = computed(() => {
        return typeof props.blok.questionID === 'string' ? props.blok.questionID.trim() || null : null;
    });

    const chartType = computed(() => (props.blok.displayMode === 'bar' ? 'bar' : 'doughnut'));

    const questionProSurveyEndpoint: string = '/api/surveys/questionpro-survey';

    const { data: surveyResponse } = await useAsyncData<AnalyticsResponse | null>(
        () => `questionpro-survey-${surveyId.value}`,
        async () => {
            if (!surveyId.value) return null;

            return await $fetch<AnalyticsResponse>(questionProSurveyEndpoint, {
                query: {
                    surveyId: surveyId.value
                },
                headers
            });
        },
        {
            watch: [surveyId],
            default: () => null
        }
    );

    const allQuestions = computed<NormalizedQuestion[]>(() => {
        return (
            surveyResponse.value?.response?.sections?.flatMap((section) => {
                return (section.questions ?? [])
                    .map((question) => normalizeQuestion(section, question))
                    .filter((question): question is NormalizedQuestion => Boolean(question));
            }) ?? []
        );
    });

    const wrapLabel = (label: string, maxLength = 18) => {
        const words = label.split(' ');
        const lines: string[] = [];
        let currentLine = '';

        words.forEach((word) => {
            const nextLine = currentLine ? `${currentLine} ${word}` : word;

            if (nextLine.length > maxLength && currentLine) {
                lines.push(currentLine);
                currentLine = word;
                return;
            }

            currentLine = nextLine;
        });

        if (currentLine) {
            lines.push(currentLine);
        }

        return lines;
    };

    const questions = computed(() => {
        if (!selectedQuestionCode.value) return allQuestions.value;

        return allQuestions.value.filter(
            (question) => question.code?.toUpperCase() === selectedQuestionCode.value?.toUpperCase()
        );
    });

    const totalPages = computed(() => {
        return Math.max(1, Math.ceil(questions.value.length / questionsPerPage));
    });

    const paginatedQuestions = computed(() => {
        const start = (currentPage.value - 1) * questionsPerPage;
        const end = start + questionsPerPage;

        return questions.value.slice(start, end);
    });

    const getChartData = (question: NormalizedQuestion): ChartData<'bar' | 'doughnut'> => {
        return {
            labels: question.answers.map((answer) => wrapLabel(answer.title)),
            datasets: [
                {
                    label: question.title,
                    data: question.answers.map((answer) => answer.count),
                    backgroundColor: question.answers.map((_, index) => colors[index % colors.length]),
                    borderColor: 'transparent',
                    borderWidth: 0,
                    hoverBorderColor: 'transparent'
                }
            ]
        };
    };

    const getChartOptions = (question: NormalizedQuestion): ChartOptions<'bar' | 'doughnut'> => {
        return {
            responsive: true,
            maintainAspectRatio: false,
            resizeDelay: 100,
            indexAxis: chartType.value === 'bar' ? 'y' : 'x',
            cutout: chartType.value === 'doughnut' ? '60%' : undefined,
            plugins: {
                legend: {
                    display: false
                },
                tooltip: {
                    callbacks: {
                        label(context) {
                            const answer = question.answers[context.dataIndex];

                            if (!answer) return '';

                            return `${answer.title}: ${answer.count} (${answer.percentage}%)`;
                        }
                    }
                }
            }
        };
    };

    const truncateChartText = (ctx: CanvasRenderingContext2D, text: string, maxWidth: number) => {
        if (ctx.measureText(text).width <= maxWidth) return text;

        let end = text.length;

        while (end > 0 && ctx.measureText(`${text.slice(0, end)}…`).width > maxWidth) {
            end -= 1;
        }

        return end > 0 ? `${text.slice(0, end)}…` : '…';
    };

    const getCenterTextPlugin = (question: NormalizedQuestion): Plugin<'doughnut'> => ({
        id: `centerText-${question.id}`,
        afterDatasetsDraw(chart) {
            const { ctx, chartArea } = chart;

            if (!chartArea) return;

            const x = (chartArea.left + chartArea.right) / 2;
            const y = (chartArea.top + chartArea.bottom) / 2;

            ctx.save();

            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';

            ctx.font = '28px sans-serif';
            ctx.fillStyle = '#333';
            ctx.fillText('🎉', x, y - 20);

            ctx.font = '400 18px Figtree, sans-serif';
            ctx.fillStyle = '#333';
            ctx.fillText(`${question.total} Stimmen für`, x, y + 10);

            ctx.font = '400 18px Figtree, sans-serif';
            ctx.fillStyle = '#333';
            const innerRadius = chart.getDatasetMeta(0).data[0]?.innerRadius ?? 0;
            const maxTextWidth = innerRadius * 2 * 0.9;
            const topAnswer = truncateChartText(ctx, question.topAnswer, maxTextWidth);
            ctx.fillText(topAnswer, x, y + 30, maxTextWidth);

            ctx.restore();
        }
    });

    const getChartPlugins = (question: NormalizedQuestion) => {
        return chartType.value === 'doughnut' ? [getCenterTextPlugin(question)] : [];
    };

    const setChartContainerRef = (questionId: number, element: unknown) => {
        chartContainerRefs.value[questionId] = element instanceof HTMLElement ? element : null;
    };

    const waitForAnimationFrame = () =>
        new Promise<void>((resolve) => {
            requestAnimationFrame(() => resolve());
        });

    const focusFirstVisibleChart = async () => {
        await nextTick();
        await waitForAnimationFrame();

        const firstQuestion = paginatedQuestions.value[0];

        if (!firstQuestion) return;

        const chartElement = chartContainerRefs.value[firstQuestion.id];

        if (!chartElement) return;

        chartElement.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
        });

        chartElement.focus({
            preventScroll: true
        });
    };

    const goToPage = async (page: number) => {
        const nextPage = Math.min(Math.max(page, 1), totalPages.value);

        if (nextPage === currentPage.value) return;

        currentPage.value = nextPage;

        await focusFirstVisibleChart();
    };

    watch([surveyId, selectedQuestionCode], () => {
        currentPage.value = 1;
    });

    watch(totalPages, () => {
        if (currentPage.value > totalPages.value) {
            currentPage.value = totalPages.value;
        }
    });
</script>

<template>
    <div
        v-if="paginatedQuestions.length"
        class="survey-result-overview"
    >
        <section
            v-for="question in paginatedQuestions"
            :key="`${question.id}-${chartType}`"
            class="survey-result-overview__question"
        >
            <h3 class="survey-result-overview__title">
                {{ question.title }}
            </h3>

            <div class="chart">
                <div
                    :ref="(element) => setChartContainerRef(question.id, element)"
                    class="chart__canvas"
                    tabindex="-1"
                    :aria-label="$t('surveyResultOverview.chartAriaLabel', { title: question.title })"
                >
                    <Doughnut
                        v-if="chartType === 'doughnut'"
                        :data="getChartData(question)"
                        :options="getChartOptions(question)"
                        :plugins="getChartPlugins(question)"
                    />

                    <Bar
                        v-else
                        :data="getChartData(question)"
                        :options="getChartOptions(question)"
                    />
                </div>

                <ul class="chart-legend">
                    <li
                        v-for="(answer, index) in question.answers"
                        :key="answer.title"
                        class="chart-legend__item"
                    >
                        <div
                            class="chart-legend__image-wrapper"
                            :class="{ 'chart-legend__image-wrapper--empty': !answer.image }"
                        >
                            <img
                                v-if="answer.image"
                                class="chart-legend__image"
                                :src="answer.image"
                                :alt="answer.title"
                            />

                            <span
                                class="chart-legend__color"
                                :class="{ 'chart-legend__color--placeholder': !answer.image }"
                                :style="{ backgroundColor: colors[index % colors.length] }"
                            />
                        </div>

                        <span class="chart-legend__label">
                            {{ answer.title }}
                        </span>
                    </li>
                </ul>
            </div>
        </section>

        <nav
            v-if="totalPages > 1"
            class="survey-result-overview__pagination"
        >
            <button
                type="button"
                class="pagination-button"
                :disabled="currentPage === 1"
                @click="goToPage(currentPage - 1)"
            >
                <span>{{ $t('general.back') }}</span>
            </button>

            <span class="page-count"> {{ currentPage }} / {{ totalPages }} </span>

            <button
                type="button"
                class="pagination-button"
                :disabled="currentPage === totalPages"
                @click="goToPage(currentPage + 1)"
            >
                <span>{{ $t('general.next') }}</span>
            </button>
        </nav>
    </div>
    <p
        v-else-if="selectedQuestionCode"
        class="survey-result-overview__empty"
    >
        {{ $t('surveyResultOverview.wrongQuestionID') }}
    </p>
</template>

<style lang="scss" scoped>
    @import '~/assets/scss/pta-family/surveys/result-overview.scss';
</style>
